import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose } from 'react-icons/md';
import InputMask from 'react-input-mask';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';



function generateCustomString() {
  const year = new Date().getFullYear(); // Mevcut yılı al
  const randomNum = Math.floor(Math.random() * 9000) + 1000; // 1000 ile 9999 arasında rastgele 4 haneli bir sayı
  const timestamp = Date.now(); // Geçerli zaman damgasını al

  return `CNT-${year}-${randomNum}-${timestamp}`;
}

// Contract tipleri için sabit değerler
const CONTRACT_TYPES = [
  { value: 'Support', label: 'Support' },
  { value: 'Project', label: 'Project' },
  { value: 'Managed Services', label: 'Managed Services' }
];

const STATUS_OPTIONS = [
  { value: 1, label: 'Active' },
  { value: 0, label: 'Deactive' }
];

const ContractSettings = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    contract_status: 1,
    contract_id: '',
    contract_name: '',
    contract_description: '',
    contract_type: '',
    ticket_count: '',
    company_account_manager: '',
    primary_technical_contact: '',
    start_date: '',
    end_date: '',
    contract_scope: '',
    customer_id: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });

  // Contract ID state'i
  const [contractId, setContractId] = useState('');

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setFormData(prev => ({
        ...prev,
        company_id: userData.companyId
      }));
      fetchContracts(userData.companyId);
      fetchCustomers(userData.companyId);
    }
  }, []);

  // URL parametresini kontrol et ve edit popup'ını aç
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('edit');
    
    if (editId && contracts.length > 0) {
      // Contract'ı bul
      const contractToEdit = contracts.find(contract => contract.id == editId);
      if (contractToEdit) {
        handleEdit(contractToEdit);
        // URL'den edit parametresini temizle
        navigate('/settings/contract', { replace: true });
      }
    }
  }, [location.search, contracts]);

  useEffect(() => {
    if (showForm) {
      const timer = setTimeout(() => {
        const input = document.querySelector('input[name="contract_name"]');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  const fetchContracts = async (companyId) => {
    try {
      const response = await api.get(`/contracts/company/${companyId}`);
      if (response.data.success) {
        setContracts(response.data.contracts);
      }
    } catch (error) {
      console.error('Kontratlar yüklenirken hata:', error);
      showPopup({
        title: t('common.error'),
        message: t('contractSettings.errorLoadingContracts'),
        type: 'error',
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async (companyId) => {
    try {
      const response = await api.get(`/customers/${companyId}`);
      if (response.data.success) {
        setCustomers(response.data.customers);
      }
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
    }
  };

  const handleDelete = async (contractId, contractName) => {
    showPopup({
      title: t('contractSettings.deleteContract'),
      message: t('contractSettings.confirmDeleteContract', { contractName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/contracts/${contractId}`);
          if (response.data.success) {
            closePopup();
            
            showPopup({
              title: t('common.success'),
              message: t('contractSettings.contractDeletedSuccess'),
              type: 'success',
              showCancel: false,
              onConfirm: () => {
                closePopup();
                const userStr = localStorage.getItem('user');
                if (userStr) {
                  const userData = JSON.parse(userStr);
                  fetchContracts(userData.companyId);
                }
              }
            });
          }
        } catch (error) {
          closePopup();
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('contractSettings.errorDeletingContract'),
            type: 'error',
            showCancel: false
          });
        }
      }
    });
  };

  const handleEdit = (contract) => {
    // Tarihleri YYYY-MM-DD formatına çevir
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      
      try {
        const date = new Date(dateStr);
        // Geçerli bir tarih mi kontrol et
        if (isNaN(date.getTime())) return '';
        
        // YYYY-MM-DD formatına çevir
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('Tarih formatlanırken hata:', error);
        return '';
      }
    };

         setFormData({
       contract_status: contract.contract_status === 1,
       contract_id: contract.contract_id || '',
       contract_name: contract.contract_name || '',
       contract_description: contract.contract_description || '',
       contract_type: contract.contract_type || '',
       ticket_count: contract.ticket_count === 0 ? 'unlimited' : (contract.ticket_count || ''),
       company_account_manager: contract.company_account_manager || '',
       primary_technical_contact: contract.primary_technical_contact || '',
       start_date: formatDate(contract.start_date),
       end_date: formatDate(contract.end_date),
       contract_scope: contract.contract_scope || '',
       customer_id: contract.customer_id || ''
     });
    setIsEditing(true);
    setEditingId(contract.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userStr = localStorage.getItem('user');
      const userData = JSON.parse(userStr);

      // Form verilerini hazırla
      const submitData = {
        ...formData,
        company_id: userData.companyId,
        // Eğer ticket_count "unlimited" ise 0 olarak gönder (önceden -1 idi)
        ticket_count: formData.ticket_count === 'unlimited' ? 0 : formData.ticket_count
      };

      let response;
      if (isEditing) {
        response = await api.put(`/contracts/${editingId}`, submitData);
      } else {
        response = await api.post('/contracts', submitData);
      }

      if (response.data.success) {
        // Form'u hemen kapat
        setShowForm(false);
        setIsEditing(false);
        setEditingId(null);
        
        // Contract listesini güncelle
        fetchContracts(userData.companyId);
        
        // Form verilerini sıfırla
        setFormData({
          contract_name: '',
          contract_description: '',
          contract_type: '',
          start_date: '',
          end_date: '',
          customer_id: '',
          contract_status: 1
        });
        
        // Popup'ı göster
        showPopup({
          title: t('common.success'),
          message: isEditing ? t('contractSettings.contractUpdatedSuccess') : t('contractSettings.contractCreatedSuccess'),
          type: 'success',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin');
      } else {
        showPopup({
          title: t('common.error'),
          message: error.response?.data?.message || t('contractSettings.errorCreatingContract'),
          type: 'error',
          showCancel: false
        });
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              value
    }));
  };

  // Popup yönetimi için fonksiyonlar
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Sayfa boyutu seçenekleri
  const pageSizeOptions = [10, 20, 50, 100, 500];

  // Sayfa boyutunu değiştirme fonksiyonu
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değişince ilk sayfaya dön
  };

  // Sayfalama hesaplamaları güncellendi
  const indexOfLastContract = currentPage * itemsPerPage;
  const indexOfFirstContract = indexOfLastContract - itemsPerPage;
  const currentContracts = contracts.slice(indexOfFirstContract, indexOfLastContract);
  const totalPages = Math.ceil(contracts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Kontrat satırına tıklandığında detay sayfasına yönlendir
  const handleContractClick = (contract) => {
    // UUID varsa UUID kullan, yoksa ID kullan (geriye uyumluluk için)
    const identifier = contract.contract_uuid || contract.id;
    navigate(`/settings/contract/${identifier}`);
  };

  // Form açıldığında Contract ID oluştur - geliştirilmiş versiyon
  useEffect(() => {
    const generateIdOnce = async () => {
      if (showForm && !isEditing) {
        try {
          // Server'da tanımlı olan doğru endpoint'i kullan
          // Eğer next-id endpoint'i yoksa, contract id'yi client-side oluştur
          const newId = generateCustomString(); // Client-side ID oluştur
          
          // Hem state hem de formData'yı güncelle
          setContractId(newId);
          setFormData(prev => ({
            ...prev,
            contract_id: newId
          }));
        } catch (error) {
          console.error('Contract ID oluşturulurken hata:', error);
        }
      }
    };
    
    generateIdOnce();
  }, [showForm, isEditing]);

  // Form kapatıldığında ID'yi resetle
  useEffect(() => {
    if (!showForm) {
      setContractId('');
    }
  }, [showForm]);

  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('contractSettings.title')}</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('contractSettings.addNewContract')}</span>
        </button>
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('contractSettings.totalContracts')}: {contracts.length}
        </span>
      </div>

      {/* Customers Table */}
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
        {/* Sayfa boyutu seçici */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex justify-between items-center rounded-tl-lg rounded-tr-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.entries')}</span>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.status')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.contractId')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.contractName')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.customer')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.type')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.startDate')}</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.endDate')}</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('contractSettings.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {currentContracts.length > 0 ? (
                currentContracts.map((contract, index) => {
                  const isLast = index === currentContracts.length - 1 && totalPages <= 1;
                  return (
                    <tr 
                      key={contract.id} 
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer`}
                      onClick={() => handleContractClick(contract)}
                    >
                      {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                        <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                          contract.contract_status 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          {contract.contract_status ? t('contractSettings.active') : t('contractSettings.inactive')}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {contract.contract_id}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {contract.contract_name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{contract.customer_name}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{contract.contract_type}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{new Date(contract.start_date).toLocaleDateString()}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">{new Date(contract.end_date).toLocaleDateString()}</td>
                      {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium ${isLast ? 'rounded-br-lg' : ''}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Satır tıklamasını engelle
                            handleEdit(contract);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          <MdEdit size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Satır tıklamasını engelle
                            handleDelete(contract.id, contract.contract_name);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <MdDelete size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="px-4 py-8 rounded-bl-lg rounded-br-lg">
                    <div className="flex justify-center items-center">
                      <p className="text-gray-400 dark:text-gray-500 text-sm">{t('contractSettings.noContractsYet')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4 p-4">
          {currentContracts.length > 0 ? (
            currentContracts.map((contract, index) => (
              <div 
                key={contract.id} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => handleContractClick(contract)}
              >
                {/* Status & Contract ID */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.status')}</div>
                    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                      contract.contract_status 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                    }`}>
                      {contract.contract_status ? t('contractSettings.active') : t('contractSettings.inactive')}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.contractId')}</div>
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{contract.contract_id}</div>
                  </div>
                </div>

                {/* Contract Name & Customer */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.contractName')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{contract.contract_name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.customer')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{contract.customer_name}</div>
                  </div>
                </div>

                {/* Type & Start Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.type')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{contract.contract_type}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.startDate')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{new Date(contract.start_date).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* End Date & Actions */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.endDate')}</div>
                    <div className="text-sm text-gray-900 dark:text-white">{new Date(contract.end_date).toLocaleDateString()}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('contractSettings.actions')}</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(contract);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                      >
                        <MdEdit size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(contract.id, contract.contract_name);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                      >
                        <MdDelete size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 dark:text-gray-500 text-sm">{t('contractSettings.noContractsYet')}</p>
            </div>
          )}
        </div>

        {/* Pagination - Alt padding artırıldı */}
        {totalPages > 1 && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('table.showing')}{' '}
                  <span className="font-medium">{indexOfFirstContract + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastContract, contracts.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{contracts.length}</span>{' '}
                  {t('contractSettings.contracts')}
                </p>
              </div>
              <div>
                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-800 text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                    }`}
                  >
                    {t('table.previous')}
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => handlePageChange(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                        currentPage === index + 1
                          ? 'bg-[#272c39] text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-800 text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                    }`}
                  >
                    {t('table.next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}


      </div>

      {/* Popup - z-index'i form modal'ından daha yüksek yapıldı */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto w-full max-w-5xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-medium">
                {isEditing ? t('contractSettings.editContract') : t('contractSettings.addNewContract')}
              </h3>
                             <button 
                 onClick={() => {
                   setShowForm(false);
                   // Form verilerini sıfırla
                   setFormData({
                     contract_status: 1,
                     contract_id: '',
                     contract_name: '',
                     contract_description: '',
                     contract_type: '',
                     ticket_count: '',
                     company_account_manager: '',
                     primary_technical_contact: '',
                     start_date: '',
                     end_date: '',
                     contract_scope: '',
                     customer_id: ''
                   });
                   setIsEditing(false);
                   setEditingId(null);
                 }}
                 className="text-white hover:text-gray-200"
               >
                 <MdClose size={20} />
               </button>
            </div>
            
            <div className="p-3 sm:p-6 lg:p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {/* Status - En başta */}
              <div className="col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contractSettings.status')}
                </label>
                <select
                  name="contract_status"
                  value={formData.contract_status}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
                  required
                >
                  {STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Contract ID - readonly */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contractSettings.contractId')}
                </label>
                <input
                  type="text"
                  name="contract_id"
                  value={formData.contract_id || contractId || ''}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md bg-gray-100 text-sm"
                  readOnly
                />
              </div>

                             {/* Diğer form alanları */}
               <div>
                 <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                   {t('contractSettings.contractName')}
                 </label>
                 <input
                   type="text"
                   name="contract_name"
                   value={formData.contract_name}
                   onChange={handleChange}
                   autoComplete="new-contract-name"
                   className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
                   required
                 />
               </div>

              {/* Customer Selection ve Contract Type yan yana */}
              <div className="col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contractSettings.customer')}
                </label>
                <select
                  name="customer_id"
                  value={formData.customer_id || ''}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                >
                  <option value="">{t('contractSettings.selectCustomer')}</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customer_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Contract Type */}
              <div className="col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contractSettings.contractType')}
                </label>
                <select
                  name="contract_type"
                  value={formData.contract_type}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
                  required
                >
                  <option value="">{t('contractSettings.selectContractType')}</option>
                  {CONTRACT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ticket Count */}
              <div className="col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('contractSettings.ticketCount')}
                </label>
                <select
                  name="ticket_count"
                  value={formData.ticket_count}
                  onChange={handleChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm"
                  required
                >
                  <option value="">{t('contractSettings.selectTicketCount')}</option>
                  <option value="unlimited">{t('contractSettings.unlimited')}</option>
                  {[10, 20, 30, 40, 50, 100].map(count => (
                    <option key={count} value={count}>
                      {count}
                    </option>
                  ))}
                </select>
              </div>

                             {/* Company Account Manager ve Primary Technical Contact yan yana */}
               <div className="col-span-1">
                 <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                   {t('contractSettings.companyAccountManager')}
                 </label>
                 <input
                   type="text"
                   name="company_account_manager"
                   value={formData.company_account_manager}
                   onChange={handleChange}
                   autoComplete="new-account-manager"
                   className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                   required
                 />
               </div>

                             <div className="col-span-1">
                 <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                   {t('contractSettings.primaryTechnicalContact')}
                 </label>
                 <input
                   type="text"
                   name="primary_technical_contact"
                   value={formData.primary_technical_contact}
                   onChange={handleChange}
                   autoComplete="new-technical-contact"
                   className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                   required
                 />
               </div>

                                                           {/* Start Date */}
                <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractSettings.startDate')}
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>

               {/* End Date - Ayrı satırda (Start Date'in hemen altında) */}
               <div className="col-span-1">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractSettings.endDate')}
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>

              {/* Contract Description - Geniş (popup genişliği kadar), Scope'un üzerinde */}
               <div className="col-span-2 lg:col-span-3">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('contractSettings.contractDescription')}
                  </label>
                  <input
                    type="text"
                    name="contract_description"
                    value={formData.contract_description}
                    onChange={handleChange}
                    autoComplete="new-contract-description"
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>

              {/* Contract Scope - En altta tam genişlik (popup genişliği kadar) */}
              <div className="col-span-2 lg:col-span-3">
                 <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                   {t('contractSettings.contractScope')}
                 </label>
                 <textarea
                   name="contract_scope"
                   value={formData.contract_scope}
                   onChange={handleChange}
                   autoComplete="new-contract-scope"
                   rows={2}
                   className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                   required
                 />
               </div>

                             {/* Form Buttons */}
               <div className="col-span-2 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-4">
                 <button
                   type="button"
                   onClick={() => {
                     setShowForm(false);
                     // Form verilerini sıfırla
                     setFormData({
                       contract_status: 1,
                       contract_id: '',
                       contract_name: '',
                       contract_description: '',
                       contract_type: '',
                       ticket_count: '',
                       company_account_manager: '',
                       primary_technical_contact: '',
                       start_date: '',
                       end_date: '',
                       contract_scope: '',
                       customer_id: ''
                     });
                     setIsEditing(false);
                     setEditingId(null);
                   }}
                   className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                 >
                   {t('common.cancel')}
                 </button>
                 <button
                   type="submit"
                   className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                 >
                   {isEditing ? t('contractSettings.updateContract') : t('contractSettings.addContract')}
                 </button>
               </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractSettings; 